// @ts-nocheck
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ChevronRight, Shield } from "lucide-react"
import AppContext from "../../AppContext.jsx"
import api from "../../api.js"
import syncDb from "../../serviceWorker/syncDb"
import AdminLocationPicker from "./AdminLocationPicker.jsx"
import AdminQuickSettings from "./AdminQuickSettings.jsx"
import { getToolbarBg } from "../helpers/getToolbarBg.js"

export default function AdminToolbar() {
    const { app, setApp } = useContext(AppContext)
    const toolbarBg = getToolbarBg()
    const [expanded, setExpanded] = useState(false)
    const [openPopout, setOpenPopout] = useState(null)
    const panelInnerRef = useRef(null)

    const lodgeLocations = useMemo(
        () => (app.locations ?? []).filter((l) => l.is_lodge),
        [app.locations]
    )

    const currentLocation = useMemo(
        () => (app.locations ?? []).find((l) => Number(l.id) === Number(app.locationId)) ?? null,
        [app.locations, app.locationId]
    )

    const usePrintDialog = Boolean(currentLocation?.use_print_dialog)

    function toggle() {
        setExpanded((prev) => {
            if (prev) {
                setOpenPopout(null)
            }
            return !prev
        })
    }

    useEffect(() => {
        if (!openPopout) {
            return
        }

        function handlePointerDown(event) {
            if (panelInnerRef.current?.contains(event.target)) {
                return
            }
            if (event.target instanceof Element && event.target.closest(".lodge-admin-toolbar-popout-menu")) {
                return
            }
            setOpenPopout(null)
        }

        document.addEventListener("click", handlePointerDown)
        return () => document.removeEventListener("click", handlePointerDown)
    }, [openPopout])

    const handleLocationChange = useCallback(async (locationId) => {
        const user = await api.updateUserLocation(locationId)
        await syncDb(true)
        setApp((prev) => ({ ...prev, locationId: user.location_id }))
    }, [setApp])

    const handleUsePrintDialogChange = useCallback(async (value) => {
        await api.updateLocationUsePrintDialog(value)
        setApp((prev) => ({
            ...prev,
            locations: (prev.locations ?? []).map((location) =>
                Number(location.id) === Number(prev.locationId)
                    ? { ...location, use_print_dialog: value }
                    : location
            ),
        }))
    }, [setApp])

    const handleUsePrintDialogChangeForLocation = useCallback(async (locationId, value) => {
        setApp((prev) => ({
            ...prev,
            locations: (prev.locations ?? []).map((location) =>
                Number(location.id) === Number(locationId)
                    ? { ...location, use_print_dialog: value }
                    : location
            ),
        }))
        try {
            await api.updateLocationUsePrintDialog(value, locationId)
        } catch (e) {
            setApp((prev) => ({
                ...prev,
                locations: (prev.locations ?? []).map((location) =>
                    Number(location.id) === Number(locationId)
                        ? { ...location, use_print_dialog: !value }
                        : location
                ),
            }))
            throw e
        }
    }, [setApp])

    const handleReloadMetadata = useCallback(async () => {
        return api.invalidateDataTableCaches()
    }, [])

    return (
        <div className={"lodge-admin-toolbar" + (expanded ? " lodge-admin-toolbar--expanded" : "")}>
            {!expanded ? (
                <button
                    type="button"
                    className="lodge-admin-toolbar-tab"
                    onClick={toggle}
                    aria-expanded={false}
                    aria-label="Show admin toolbar"
                    title="Show admin toolbar"
                >
                    <Shield size={18} aria-hidden="true" />
                </button>
            ) : null}
            <div className="lodge-admin-toolbar-panel" role="toolbar" aria-label="Admin tools" aria-hidden={!expanded}>
                <div className="lodge-admin-toolbar-panel-inner" ref={panelInnerRef}>
                    <button
                        type="button"
                        className="lodge-admin-toolbar-lead"
                        onClick={toggle}
                        aria-expanded={true}
                        aria-label="Hide admin toolbar"
                        title="Hide admin toolbar"
                        tabIndex={expanded ? 0 : -1}
                    >
                        <span className="lodge-admin-toolbar-lead-icon" aria-hidden="true">
                            <Shield className="lodge-admin-toolbar-lead-icon-shield" size={18} />
                            <ChevronRight className="lodge-admin-toolbar-lead-icon-chevron" size={18} />
                        </span>
                        <span className="lodge-admin-toolbar-label">Admin</span>
                    </button>
                    <AdminLocationPicker
                        locations={lodgeLocations}
                        value={app.locationId}
                        onChange={handleLocationChange}
                        disabled={!expanded}
                        isOpen={openPopout === "location"}
                        onOpenChange={(open) => setOpenPopout(open ? "location" : null)}
                        toolbarBg={toolbarBg}
                    />
                    <AdminQuickSettings
                        usePrintDialog={usePrintDialog}
                        onUsePrintDialogChange={handleUsePrintDialogChange}
                        onUsePrintDialogChangeForLocation={handleUsePrintDialogChangeForLocation}
                        onReloadMetadata={handleReloadMetadata}
                        locations={lodgeLocations}
                        currentLocationId={app.locationId}
                        disabled={!expanded}
                        isOpen={openPopout === "settings"}
                        onOpenChange={(open) => setOpenPopout(open ? "settings" : null)}
                        lodgeCode={currentLocation?.lodge_code ?? "—"}
                        toolbarBg={toolbarBg}
                    />
                    <div className="lodge-admin-toolbar-end" aria-hidden="true" />
                </div>
            </div>
        </div>
    )
}
